import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Trash2, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { reduceYears, getStatusColor, ucFirst } from "@/utils";
import type { Booking, Contract, ContractStatus } from "@/types";
import { CustomBookingModal } from "@/components/CustomBookingModal";
import { useState, useMemo } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface BookingsCardProps {
  contract: Contract;
  bookings: Booking[];
  onDeleteBooking: (bookingId: number) => void;
  onAddCustomBooking: (booking: Omit<Booking, '_id'>) => void;
  disabled?: boolean; // New prop to disable the card
}

export function BookingsCard({
  contract,
  bookings,
  onDeleteBooking,
  onAddCustomBooking,
  disabled = false // Default to false
}: BookingsCardProps) {
  const [isCustomBookingModalOpen, setIsCustomBookingModalOpen] = useState(false);
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const displayBookings = bookings.length > 0 ? bookings : (contract.bookings || []);

  // Check if contract status is active or complete to disable editing
  const isDisabled = disabled || contract.status === 'active' || contract.status === 'complete';

  const handleDeleteBookingClick = (bookingId: number) => {
    if (isDisabled) return; // Prevent action if disabled
    setBookingToDelete(bookingId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteBooking = async () => {
    if (!bookingToDelete || isDisabled) return;

    try {
      setIsDeletingBooking(true);
      onDeleteBooking(bookingToDelete);
    } finally {
      setIsDeletingBooking(false);
      setIsDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center">
          <CardTitle>Bookings</CardTitle>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCustomBookingModalOpen(true)}
          className="gap-2"
          disabled={isDisabled} // Use the combined disabled state
        >
          <PlusCircle className="h-4 w-4" />
          Add Custom Booking
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[200px] rounded-md">
          <div className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Resort</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No bookings found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayBookings.map((booking) => (
                    <TableRow key={booking._id}>
                      <TableCell>{booking.unitNumber || 'N/A'}</TableCell>
                      <TableCell>{booking.resort || 'N/A'}</TableCell>
                      <TableCell>
                        {reduceYears(booking.years).map((year, index) => (
                          <Badge key={`${booking._id}-${year}-${index}`} variant="secondary" className="mr-1">
                            {year}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell>Week {booking.week}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getStatusColor(booking.status)}>
                          {ucFirst(booking.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {booking.isCustom ? (
                          <Badge variant="outline">Custom ({booking.type})</Badge>
                        ) : (
                          <Badge variant="secondary">{booking.type || 'Standard'}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteBookingClick(booking._id!)}
                          className="text-destructive hover:text-destructive/90"
                          disabled={isDisabled} // Use the combined disabled state
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </CardContent>

      <CustomBookingModal
        isOpen={isCustomBookingModalOpen}
        onClose={() => setIsCustomBookingModalOpen(false)}
        onSubmit={onAddCustomBooking}
        contractId={contract._id}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteBooking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingBooking || isDisabled} // Use the combined disabled state
            >
              {isDeletingBooking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
